import type { PageModule } from "./page-module"

/**
 * ビューポートに入った要素に is-inview を付与する汎用オブザーバ
 * 対象: [data-inview]（一度入ったら監視解除）。CSS側で is-inview に演出を当てる。
 * @example
 * new InView();
 */
export class InView {
  targets: HTMLElement[]
  observer: IntersectionObserver | null = null

  constructor(scope: ParentNode = document) {
    this.targets = Array.from(scope.querySelectorAll<HTMLElement>("[data-inview]"))
    if (!this.targets.length) return

    // 非対応環境では即表示
    if (!("IntersectionObserver" in window)) {
      this.targets.forEach((el) => el.classList.add("is-inview"))
      return
    }

    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.1,
    })
    this.targets.forEach((el) => this.observer?.observe(el))
  }

  /**
   * 交差したら is-inview を付与して監視解除
   * @private
   * @param {IntersectionObserverEntry[]} entries - 交差エントリ
   */
  onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      entry.target.classList.add("is-inview")
      this.observer?.unobserve(entry.target)
    })
  }

  /**
   * IntersectionObserver を解放して監視を停止する
   * @returns {void}
   */
  disconnect = (): void => {
    this.observer?.disconnect()
    this.observer = null
  }
}

/**
 * 現在ページの <main>（scope）内の [data-inview] を監視する InView をマウントする
 * @param {HTMLElement} scope - 対象要素を探索する起点（現在ページの <main>）
 * @returns {(() => void) | void} 後始末関数（対象が無ければ何もしない）
 */
export const mountInview: PageModule = (scope) => {
  if (!scope.querySelector("[data-inview]")) return
  const inst = new InView(scope)
  return () => inst.disconnect()
}
